/**
 * G15. Старость 70+: «Память». Раз в три года — шесть карточек-воспоминаний
 * из собственного журнала (три пары: одна запись — две карточки), найти три
 * пары. В приоритете ☆-годы (`LogEntry.starred`), затем остальные лёгкие
 * записи; тяжёлые (`heavy`) не показываются; если записей меньше трёх —
 * карточки-заглушки `minigame.memory.filler.<i>`. При состоянии `dementia`
 * (движок его пока не ставит — план 3+) карточки «плывут» (`state.drift`,
 * рисует клиент; логика та же). Все три пары — счастье +2; иначе ничего
 * плохого, только текст. «Пропустить» — без счастья, текст «quiet».
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, LogEntry, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { shuffle } from '../skill/common';
import { nothing } from './common';

export const MEMORY_MIN_AGE = 70;
export const MEMORY_MAX_AGE = 120;
export const MEMORY_EVERY_YEARS = 3;
export const MEMORY_PAIRS = 3;
export const MEMORY_CARDS = MEMORY_PAIRS * 2;
export const MEMORY_HAPPINESS = 2;
/** Состояние «деменция» (условие жизни); здесь только читается. */
export const CONDITION_DEMENTIA = 'dementia';
export const MEMORY_FILLERS = 3;

export interface MemoryCard {
  /** Номер пары 0..MEMORY_PAIRS-1. */
  pair: number;
  age: number;
  /** Ключ записи журнала (или заглушки) с параметрами. */
  key: string;
  params?: Record<string, string | number>;
}

export interface MemoryState {
  cards: MemoryCard[];
  pairs: number;
  /** Деменция: карточки «плывут». */
  drift: boolean;
}

/** Пары индексов карточек, которые игрок открыл вместе. */
export type MemoryInput = { pairs: [number, number][] };

export function memoryFillerKey(i: number): string {
  return `minigame.memory.filler.${i}`;
}

/** Записи-кандидаты: сначала ☆, потом остальные лёгкие; порядок внутри групп — по `rng`. */
export function memoryCandidates(log: readonly LogEntry[], rng: Rng): LogEntry[] {
  const light = log.filter((e) => !e.heavy);
  const starred = shuffle(
    light.filter((e) => e.starred === true),
    rng,
  );
  const rest = shuffle(
    light.filter((e) => e.starred !== true),
    rng,
  );
  return [...starred, ...rest];
}

/** Сколько пар собрано: разные пары, оба индекса в одной паре. */
export function memoryFound(state: MemoryState, input: MemoryInput): number {
  const done = new Set<number>();
  for (const pair of Array.isArray(input.pairs) ? input.pairs : []) {
    if (!Array.isArray(pair)) continue;
    const [a, b] = pair;
    const ca = Number.isInteger(a) ? state.cards[a as number] : undefined;
    const cb = Number.isInteger(b) ? state.cards[b as number] : undefined;
    if (ca && cb && a !== b && ca.pair === cb.pair) done.add(ca.pair);
  }
  return done.size;
}

export const memory: MinigameDef<MemoryInput, MemoryState> = {
  id: 'memory',
  section: 'G',
  triggers: [{ kind: 'age', min: MEMORY_MIN_AGE, max: MEMORY_MAX_AGE, everyYears: MEMORY_EVERY_YEARS }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return life.age >= MEMORY_MIN_AGE;
  },
  setup(life: Life, _ds: Dataset, rng: Rng): MemoryState {
    const picked = memoryCandidates(life.log, rng).slice(0, MEMORY_PAIRS);
    const cards: MemoryCard[] = [];
    for (let p = 0; p < MEMORY_PAIRS; p += 1) {
      const entry = picked[p];
      const card: MemoryCard = entry
        ? { pair: p, age: entry.age, key: entry.key, ...(entry.params ? { params: entry.params } : {}) }
        : { pair: p, age: rng.int(5, Math.max(5, life.age - 1)), key: memoryFillerKey(p % MEMORY_FILLERS) };
      cards.push(card, { ...card });
    }
    return { cards: shuffle(cards, rng), pairs: MEMORY_PAIRS, drift: life.conditions.includes(CONDITION_DEMENTIA) };
  },
  play(state: MemoryState, input: MemoryInput): MinigameOutcome {
    const found = memoryFound(state, input);
    if (found >= state.pairs) return { ...nothing(), stats: { happiness: MEMORY_HAPPINESS }, logKey: 'minigame.memory.found' };
    return { ...nothing(), logKey: 'minigame.memory.faded', logParams: { found } };
  },
  expected(): MinigameOutcome {
    return { ...nothing(), logKey: 'minigame.memory.quiet' };
  },
};
